import { useRef, useState, useEffect, ReactNode } from "react";
import { cx, useTabIsActive, useTranslations } from "@jugl-web/utils";
import {
  PageLoaderFull,
  PlainButton,
  Menu,
} from "@jugl-web/ui-components/cross-platform";
import { MultiSectionLayout } from "@jugl-web/ui-components/cross-platform/content/MultiSectionLayout";
import { PRIVACY_POLICY_URL, TERMS_OF_USE_URL } from "@jugl-web/utils/consts";
import { useUserAuth } from "../../providers/UserAuthProvider/useUserAuth";

import background from "./assets/background.png";
import persona1Image from "./assets/persona-step-1.jpg";
import persona2Image from "./assets/persona-step-2.jpg";
import persona3Image from "./assets/persona-step-3.png";
import persona4Image from "./assets/persona-step-4.jpg";
import persona5Image from "./assets/persona-step-5.jpg";
import persona6Image from "./assets/persona-step-6.png";
import persona7Image from "./assets/persona-step-7.jpg";

import { ReactComponent as PlayIcon } from "./assets/play.svg";
import { ReactComponent as PauseIcon } from "./assets/pause.svg";
import { ProgressBar } from "../../components/ProgressBar";
import { ReactComponent as BackArrowIcon } from "./assets/back-arrow.svg";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ThreeDotsIcon } from "./assets/three-dots.svg";
import { ReactComponent as LogutIcon } from "./assets/menu-logout.svg";
import { ReactComponent as DeleteMyAccountIcon } from "./assets/menu-delete-my-account.svg";
import { useDeleteAccountDialog } from "../../components/DeleteAccountDialog/useDeleteAccountDialog";

type StepKey =
  | "login"
  | "profileForm"
  | "invitations"
  | "noInvitations"
  | "signUpPurpose"
  | "newWorkspace"
  | "setupWorkspace";

type StepsType = {
  [key in StepKey]: {
    video?: string;
    videoClassName?: string;
    image?: string;
    thumbnail?: string;
  };
};

export const SignUpFlowLayout = ({
  type,
  children,
  isLoading = false,
  showTermsAndConditions,
  progressBar,
  footerClassName,
  showMoreMenu = false,
  onBackClick,
  onClose,
}: {
  type: StepKey;
  children: React.ReactNode;
  isLoading?: boolean;
  showTermsAndConditions?: boolean;
  progressBar?: number;
  footerClassName?: string;
  showMoreMenu?: boolean;
  onBackClick?: () => void;
  onClose?: () => void;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [wasPlaying, setWasPlaying] = useState(false);
  const { open } = useDeleteAccountDialog();

  useEffect(() => {
    if (isPlaying && !wasPlaying) {
      setWasPlaying(true);
    }
  }, [isPlaying, wasPlaying]);

  const { t } = useTranslations();

  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const isTabActive = useTabIsActive();
  const { logout } = useUserAuth();
  const steps: StepsType = {
    login: {
      video: "https://jugl-assests.jugl.com/signupvideos/Step-1.mp4",
      thumbnail: persona1Image,
    },
    profileForm: {
      video: "https://jugl-assests.jugl.com/signupvideos/Step-2.mp4",
      thumbnail: persona2Image,
    },
    invitations: {
      image: persona3Image,
    },
    signUpPurpose: {
      video: "https://jugl-assests.jugl.com/signupvideos/Step-3a.mp4",
      thumbnail: persona4Image,
    },
    noInvitations: {
      video: "https://jugl-assests.jugl.com/signupvideos/Step-3b.mp4",
      thumbnail: persona5Image,
    },
    newWorkspace: {
      thumbnail: persona6Image,
      video: "https://jugl-assests.jugl.com/signupvideos/Step-4.mp4",
    },
    setupWorkspace: {
      video: "https://jugl-assests.jugl.com/signupvideos/Step-5.mp4",
      thumbnail: persona7Image,
    },
  };

  const isImage = steps[type]?.image;
  const isVideo = steps[type]?.video;

  useEffect(() => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.pause();
    }
    setIsPlaying(false);
  }, [type]);

  useEffect(() => {
    if (!isTabActive) {
      if (videoPlayerRef.current) {
        videoPlayerRef.current.pause();
      }
      setIsPlaying(false);
    }
  }, [isTabActive]);

  return (
    <div className="relative z-[101] flex min-h-screen min-w-screen items-center justify-center overflow-hidden p-8">
      <PageLoaderFull isTransparent isActive={isLoading} />
      <div
        className="fixed inset-0 z-0 flex items-center justify-center backdrop-blur-sm"
        style={{
          WebkitBackdropFilter: "blur(4px)",
          backdropFilter: "blur(4px)",
        }}
      />
      <img
        src={background}
        alt="Background"
        className="absolute inset-0 z-0 m-0 h-full min-h-screen w-full min-w-full object-cover"
        style={{
          width: "100%",
          height: "100%",
          maxWidth: "none",
        }}
      />
      <div
        className={cx(
          "z-10 h-[626px] w-[1046px] rounded-2xl bg-white 2xl:h-[783px] 2xl:w-[1310px]"
        )}
        style={{
          boxShadow: "0px 24px 56px 0px rgba(0, 0, 0, 0.24)",
        }}
      >
        <MultiSectionLayout
          classNames={{
            footer: cx("px-[64px]", footerClassName),
          }}
          footer={
            <div className="flex w-full justify-center">
              {showTermsAndConditions && (
                <div className="z-10 w-[300px] text-center text-sm text-grey-700">
                  {t(
                    {
                      id: "auth.terms-and-privacy",
                      defaultMessage: `<termsLink>Terms and Conditions</termsLink> and <privacyLink>Privacy Policy</privacyLink>`,
                    },
                    {
                      termsLink: (text: ReactNode) => (
                        <a
                          className="text-primary-300"
                          href={TERMS_OF_USE_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {text}
                        </a>
                      ),
                      privacyLink: (text: ReactNode) => (
                        <a
                          className="text-primary-300"
                          href={PRIVACY_POLICY_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {text}
                        </a>
                      ),
                    }
                  )}
                </div>
              )}
              {progressBar?.toString() && (
                <ProgressBar progress={progressBar} />
              )}
            </div>
          }
          leftSide={
            <div className="z-10 h-full">
              {isImage && (
                <div
                  className="relative hidden h-full w-[378px] lg:block 2xl:w-full 2xl:max-w-[532px] 2xl:min-w-[532px]"
                  style={{
                    backgroundImage: `url(${steps[type]?.image})`,
                    backgroundSize: "cover",
                  }}
                />
              )}

              {isVideo && (
                <div className="relative hidden h-full w-[378px] shrink-0 cursor-pointer overflow-hidden rounded-2xl lg:block 2xl:w-[532px] 2xl:min-w-[532px]">
                  {steps[type]?.thumbnail && !wasPlaying && (
                    <img
                      src={steps[type]?.thumbnail}
                      alt="Thumbnail"
                      className="absolute top-0 left-0 m-0 h-full w-auto max-w-none rounded-2xl object-fill"
                    />
                  )}
                  <video
                    ref={videoPlayerRef}
                    poster={steps[type]?.thumbnail}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (videoPlayerRef.current) {
                        if (isPlaying) {
                          videoPlayerRef.current.pause();
                        } else {
                          videoPlayerRef.current.play();
                        }
                        setIsPlaying(!isPlaying);
                      }
                    }}
                    src={steps[type]?.video}
                    onEnded={() => setIsPlaying(false)}
                    playsInline
                    className={cx(
                      "absolute top-0 left-0 m-0 h-full w-auto max-w-none rounded-2xl object-fill",
                      steps[type]?.videoClassName
                    )}
                  />
                  <div
                    className={cx(
                      "absolute inset-0 flex items-center justify-center transition-opacity duration-300",
                      isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (videoPlayerRef.current) {
                        if (isPlaying) {
                          videoPlayerRef.current.pause();
                        } else {
                          videoPlayerRef.current.play();
                        }
                        setIsPlaying(!isPlaying);
                      }
                    }}
                  >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                  </div>
                </div>
              )}
            </div>
          }
        >
          <div className="relative z-20 flex h-full w-full justify-center gap-[10px] max-lg:justify-center">
            <div className="z-30 ml-0 flex w-full justify-center">
              {onBackClick && (
                <PlainButton
                  className="absolute top-[36px] left-[64px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-md bg-grey-200 transition-colors duration-300 hover:brightness-95"
                  onClick={onBackClick}
                >
                  <BackArrowIcon />
                </PlainButton>
              )}
              {children}
              {onClose && (
                <PlainButton
                  className="absolute top-[36px] right-[36px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-md transition-colors duration-300 hover:brightness-95"
                  onClick={onClose}
                >
                  <CloseIcon />
                </PlainButton>
              )}
              {showMoreMenu && (
                <Menu
                  placement="bottom-end"
                  className="py-2"
                  sections={[
                    [
                      {
                        id: "logout",
                        label: t({
                          id: "signup-Layout.logout",
                          defaultMessage: "Log out",
                        }),
                        icon: <LogutIcon />,
                        labelClassName: "text-base text-dark tracking-0 px-0",
                        onSelect: (_, close) => {
                          close();
                          logout(true);
                        },
                      },
                      {
                        id: "delete-account",
                        label: t({
                          id: "signup-Layout.delete-my-account",
                          defaultMessage: "Delete my Account",
                        }),
                        icon: <DeleteMyAccountIcon />,
                        labelClassName:
                          "text-tertiary-800 text-base tracking-0 px-0",
                        onSelect: (_, close) => {
                          open();
                          close();
                        },
                      },
                    ],
                  ]}
                  renderTrigger={({ Trigger, triggerRef, isOpen }) => (
                    <Trigger
                      as={PlainButton}
                      ref={triggerRef}
                      className={cx(
                        "absolute top-[36px] right-[36px] z-10 flex h-[40px] w-[40px] items-center justify-center rounded-md transition-colors duration-300 hover:brightness-95",
                        {
                          "bg-grey-200": isOpen,
                        }
                      )}
                    >
                      <ThreeDotsIcon />
                    </Trigger>
                  )}
                />
              )}
            </div>
          </div>
        </MultiSectionLayout>
      </div>
    </div>
  );
};
